import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { User, AIMessage, ToolCallInfo, ResponseStyle } from '../../types';
import RPECard from './RPECard';
import TriggerAlert, { TriggerAlertData } from './TriggerAlert';
import WeeklyReview from './WeeklyReview';
import './AICoachChat.css';

interface AICoachChatProps {
  user: User;
  onWorkoutCreated: () => void;
  showWeeklyReview: boolean;
  setShowWeeklyReview: (show: boolean) => void;
  pendingMessage?: string | null;
  onPendingMessageConsumed?: () => void;
}

const AICoachChat: React.FC<AICoachChatProps> = ({ user, onWorkoutCreated, showWeeklyReview, setShowWeeklyReview, pendingMessage, onPendingMessageConsumed }) => {
  const { t } = useLanguage();
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [toolCalls, setToolCalls] = useState<ToolCallInfo[]>([]);
  const [images, setImages] = useState<{ data: string; media_type: string }[]>([]);
  const [chartImages, setChartImages] = useState<{ data: string; media_type: string; chart_type: string; activity_name: string }[]>([]);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [unratedActivity, setUnratedActivity] = useState<{ id: number; name: string; distance_km: number | null; duration: string; date: string } | null>(null);
  const [rpeCardVisible, setRpeCardVisible] = useState(false);
  const [triggerAlerts, setTriggerAlerts] = useState<TriggerAlertData[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamStartRef = useRef<number>(0);
  // Typing animation buffer
  const typeBufferRef = useRef('');         // full content received so far
  const typeDisplayedRef = useRef('');      // content rendered so far
  const typeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const typeMsgIdRef = useRef<string>('');  // current assistant message id
  const typeStreamDoneRef = useRef(false);  // true when SSE stream finished

  // Load history on mount
  useEffect(() => {
    loadHistory();
    fetchUnratedActivity();
    fetchTriggerAlerts();
  }, []);

  const fetchUnratedActivity = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai-coach/unrated-activity`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.activity) {
          const dismissed = localStorage.getItem(`rpe_dismissed_${data.activity.id}`);
          if (!dismissed) {
            setUnratedActivity(data.activity);
            setRpeCardVisible(true);
          }
        }
      }
    } catch (e) {
      console.error('Failed to fetch unrated activity:', e);
    }
  };

  const handleRpeSubmit = () => {
    setRpeCardVisible(false);
    setUnratedActivity(null);
    fetchUnratedActivity();
  };

  const handleRpeDismiss = () => {
    if (unratedActivity) {
      localStorage.setItem(`rpe_dismissed_${unratedActivity.id}`, 'true');
    }
    setRpeCardVisible(false);
    setUnratedActivity(null);
  };

  const fetchTriggerAlerts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai-coach/trigger-alerts`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const alerts = Array.isArray(data) ? data : (data.alerts || []);
        setTriggerAlerts(alerts);
      }
    } catch (e) {
      console.error('Failed to fetch trigger alerts:', e);
    }
  };

  const handleTriggerAdjust = (alert: TriggerAlertData) => {
    const descriptions: Record<string, string> = {
      rpe_mismatch: 'RPE/intensity mismatch suggesting possible fatigue',
      cumulative_fatigue: 'cumulative fatigue from consecutive hard efforts',
      hrv_crash: 'significant HRV drop',
      sleep_deprivation: 'sleep deprivation',
    };
    const desc = descriptions[alert.trigger_type] || alert.trigger_type;
    setInput(`I got an alert about ${desc}. Can you help me adjust my upcoming workouts?`);
    setTriggerAlerts(prev => prev.filter(a => a.id !== alert.id));
  };

  const handleTriggerDismiss = (alertId: number) => {
    setTriggerAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const handleWeeklyReviewDiscuss = (context: string) => {
    setShowWeeklyReview(false);
    setInput(context);
  };

  const handleWeeklyReviewGenerate = () => {
    setShowWeeklyReview(false);
    sendMessage('Please generate my training plan for next week');
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setMessages(data.filter((m: AIMessage) => m.role === 'user' || m.role === 'assistant'));
        }
      }
      // Also get current session
      const sRes = await fetch(`${API_BASE_URL}/api/ai/sessions/current`, { credentials: 'include' });
      if (sRes.ok) {
        const sData = await sRes.json();
        if (sData.session) {
          setSessionId(sData.session.id || sData.id);
        }
      }
    } catch (e) {
      console.error('Failed to load AI history:', e);
    }
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isStreaming) scrollToBottom();
  }, [messages, isStreaming, scrollToBottom]);

  // Scroll chat container to bottom after history loads (without moving page)
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    if (messages.length > 0 && !historyLoadedRef.current) {
      historyLoadedRef.current = true;
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 50);
    }
  }, [messages]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setShowScrollBtn(!atBottom);
  }, []);

  const startTypingAnimation = useCallback((msgId: string) => {
    typeMsgIdRef.current = msgId;
    typeBufferRef.current = '';
    typeDisplayedRef.current = '';
    typeStreamDoneRef.current = false;

    if (typeIntervalRef.current) clearInterval(typeIntervalRef.current);

    typeIntervalRef.current = setInterval(() => {
      const full = typeBufferRef.current;
      const displayed = typeDisplayedRef.current;

      if (displayed.length >= full.length) {
        // Buffer caught up — if stream is done, stop interval
        if (typeStreamDoneRef.current) {
          if (typeIntervalRef.current) clearInterval(typeIntervalRef.current);
          typeIntervalRef.current = null;
        }
        return;
      }

      // Render 2-4 chars per tick for natural speed (~150-300 chars/sec at 15ms interval)
      const remaining = full.length - displayed.length;
      const charsPerTick = remaining > 80 ? 4 : remaining > 30 ? 3 : 2;
      const next = full.slice(0, displayed.length + charsPerTick);
      typeDisplayedRef.current = next;

      setMessages(prev => {
        const existing = prev.find(m => m.id === typeMsgIdRef.current);
        if (existing) {
          return prev.map(m => m.id === typeMsgIdRef.current ? { ...m, content: next } : m);
        }
        return [...prev, {
          id: typeMsgIdRef.current,
          role: 'assistant' as const,
          content: next,
          timestamp: new Date().toISOString(),
        }];
      });
    }, 15);
  }, []);

  const stopTypingAnimation = useCallback(() => {
    typeStreamDoneRef.current = true;
    // Flush remaining content immediately if interval already stopped
    if (!typeIntervalRef.current && typeDisplayedRef.current.length < typeBufferRef.current.length) {
      const final = typeBufferRef.current;
      typeDisplayedRef.current = final;
      setMessages(prev =>
        prev.map(m => m.id === typeMsgIdRef.current ? { ...m, content: final } : m)
      );
    }
  }, []);

  // Cleanup typing interval on unmount
  useEffect(() => {
    return () => {
      if (typeIntervalRef.current) clearInterval(typeIntervalRef.current);
    };
  }, []);

  const getResponseStyle = (): ResponseStyle => {
    return (localStorage.getItem('ai_coach_response_style') as ResponseStyle) || 'pogacar_calm_pro';
  };

  const resizeImage = (file: File): Promise<{ data: string; media_type: string }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX = 2048;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          const base64 = dataUrl.split(',')[1];
          resolve({ data: base64, media_type: 'image/jpeg' });
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) return;
    const resized = await resizeImage(file);
    setImages(prev => [...prev, resized]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (idx: number) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text && images.length === 0) return;
    if (isStreaming) return;

    const userMsg: AIMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      attachments: images.map(img => ({ type: 'image' as const, media_type: img.media_type, data: img.data })),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setImages([]);
    setIsStreaming(true);
    setStatusText(t('aiCoach.chat.thinking'));
    setToolCalls([]);
    setChartImages([]);
    streamStartRef.current = Date.now();

    const abort = new AbortController();
    abortRef.current = abort;

    // Build request body
    const body: any = {
      message: text,
      response_style: getResponseStyle(),
      request_id: `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (images.length > 0) {
      body.attachments = images.map(img => ({
        type: 'image',
        media_type: img.media_type,
        data: img.data,
      }));
    }

    const assistantMsgId = `assistant-${Date.now()}`;
    startTypingAnimation(assistantMsgId);

    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
        signal: abort.signal,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No reader');

      const decoder = new TextDecoder();
      let buffer = '';

      // Set up timeout warnings
      const timeouts: NodeJS.Timeout[] = [];
      timeouts.push(setTimeout(() => setStatusText(t('aiCoach.chat.stillWorking')), 5000));
      timeouts.push(setTimeout(() => setStatusText(t('aiCoach.chat.takingLong')), 15000));

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);

            switch (event.type) {
              case 'session':
                if (event.session_id) setSessionId(event.session_id);
                break;

              case 'status':
                if (event.status === 'thinking') setStatusText(t('aiCoach.chat.thinking'));
                break;

              case 'chunk':
                typeBufferRef.current += event.text || '';
                break;

              case 'tool_call_started':
                setToolCalls(prev => [...prev, {
                  id: event.call_id || event.id || String(Date.now()),
                  tool: event.tool_name || event.tool || 'unknown',
                  status: 'running',
                  startTime: Date.now(),
                }]);
                break;

              case 'tool_call_finished':
                setToolCalls(prev => prev.map(tc =>
                  (tc.id === event.call_id || tc.id === event.id)
                    ? { ...tc, status: 'completed', durationMs: Date.now() - tc.startTime }
                    : tc
                ));
                break;

              case 'workout_saved':
                onWorkoutCreated();
                break;

              case 'chart':
                if (event.data) {
                  setChartImages(prev => [...prev, {
                    data: event.data,
                    media_type: event.media_type || 'image/png',
                    chart_type: event.chart_type || '',
                    activity_name: event.activity_name || '',
                  }]);
                }
                break;

              case 'done':
                timeouts.forEach(clearTimeout);
                stopTypingAnimation();
                break;

              case 'error':
                setStatusText(event.message || t('aiCoach.chat.error'));
                break;
            }
          } catch {
            // Skip unparseable lines
          }
        }
      }

      timeouts.forEach(clearTimeout);
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        setMessages(prev => {
          const existing = prev.find(m => m.id === assistantMsgId);
          if (!existing) {
            return [...prev, {
              id: assistantMsgId,
              role: 'assistant',
              content: t('aiCoach.chat.error'),
              timestamp: new Date().toISOString(),
            }];
          }
          return prev;
        });
      }
    } finally {
      stopTypingAnimation();
      setIsStreaming(false);
      setStatusText('');
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendChipMessage = (text: string) => {
    sendMessage(text);
  };

  // Handle pending message from other tabs (e.g. generate plan from Goals)
  useEffect(() => {
    if (pendingMessage && !isStreaming) {
      sendMessage(pendingMessage);
      onPendingMessageConsumed?.();
    }
  }, [pendingMessage]); // eslint-disable-line

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const toolLabel = (toolName: string): string => {
    const key = `aiCoach.tools.${toolName}`;
    const translated = t(key);
    return translated !== key ? translated : t('aiCoach.tools.unknown');
  };

  // Filter out session context summary messages (injected by backend for AI continuity)
  const isContextMessage = (msg: AIMessage) =>
    (msg.role === 'user' && msg.content.includes('[Previous conversation context')) ||
    (msg.role === 'assistant' && msg.content.startsWith('Understood, I have the context'));
  const visibleMessages = messages.filter(m => !isContextMessage(m));

  return (
    <div className="ai-chat">
      {/* RPE ride rating card removed — not needed */}

      {triggerAlerts.map(alert => (
        <TriggerAlert
          key={alert.id}
          alert={alert}
          onAdjust={() => handleTriggerAdjust(alert)}
          onDismiss={() => handleTriggerDismiss(alert.id)}
        />
      ))}

      {visibleMessages.length === 0 && !isStreaming ? (
        <div className="ai-chat-empty">
          <div className="ai-chat-empty-icon">&#129504;</div>
          <h3>{t('aiCoach.chat.empty')}</h3>
          <p>{t('aiCoach.chat.emptySubtitle')}</p>
          <div className="ai-chat-chips">
            <button className="ai-chat-chip" onClick={() => sendChipMessage(t('aiCoach.chat.chips.analyzeWeek'))}>
              {t('aiCoach.chat.chips.analyzeWeek')}
            </button>
            <button className="ai-chat-chip" onClick={() => sendChipMessage(t('aiCoach.chat.chips.trainingTips'))}>
              {t('aiCoach.chat.chips.trainingTips')}
            </button>
            <button className="ai-chat-chip" onClick={() => sendChipMessage(t('aiCoach.chat.chips.recovery'))}>
              {t('aiCoach.chat.chips.recovery')}
            </button>
            <button className="ai-chat-chip" onClick={() => sendChipMessage(t('aiCoach.chat.chips.goals'))}>
              {t('aiCoach.chat.chips.goals')}
            </button>
          </div>
        </div>
      ) : (
        <div className="ai-chat-messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {visibleMessages.map((msg) => (
            <div key={msg.id} className={`ai-chat-message ${msg.role}`}>
              <div className="ai-chat-avatar">
                {msg.role === 'user' ? user.first_name[0] : 'AI'}
              </div>
              <div className="ai-chat-bubble">
                {msg.role === 'assistant' ? (
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                ) : (
                  <>
                    {msg.content && <p>{msg.content}</p>}
                    {msg.attachments?.map((att, i) => (
                      att.data && <img key={i} src={`data:${att.media_type};base64,${att.data}`} alt="" className="ai-chat-image-preview" />
                    ))}
                  </>
                )}
              </div>
            </div>
          ))}

          {chartImages.map((chart, i) => (
            <div key={i} className="ai-chat-chart">
              <img
                src={`data:${chart.media_type};base64,${chart.data}`}
                alt={`${chart.chart_type} chart${chart.activity_name ? ` — ${chart.activity_name}` : ''}`}
                className="ai-chat-chart-img"
              />
            </div>
          ))}

          {showScrollBtn && (
            <button className="ai-chat-scroll-btn" onClick={scrollToBottom}>&#8595;</button>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Tool call pills */}
      {toolCalls.length > 0 && (
        <div className="ai-chat-tools">
          {toolCalls.map(tc => (
            <span key={tc.id} className={`tool-pill ${tc.status}`}>
              {tc.status === 'running' && <span className="tool-pill-spinner" />}
              {tc.status === 'completed' && <span>&#10003;</span>}
              {tc.status === 'failed' && <span>&#10007;</span>}
              {toolLabel(tc.tool)}
              {tc.durationMs && <span className="tool-pill-duration">{(tc.durationMs / 1000).toFixed(1)}s</span>}
            </span>
          ))}
        </div>
      )}

      {/* Status */}
      {isStreaming && (
        <div className="ai-chat-status">
          <span className="ai-chat-status-spinner" />
          <span>{statusText}</span>
          {Date.now() - streamStartRef.current > 30000 && (
            <button className="ai-chat-cancel" onClick={handleCancel}>{t('aiCoach.chat.cancel')}</button>
          )}
        </div>
      )}

      {/* Input area */}
      <div className="ai-chat-input-area">
        {images.length > 0 && (
          <div className="ai-chat-image-strip">
            {images.map((img, i) => (
              <div key={i} className="ai-chat-image-thumb">
                <img src={`data:${img.media_type};base64,${img.data}`} alt="" />
                <button onClick={() => removeImage(i)}>&times;</button>
              </div>
            ))}
          </div>
        )}
        <div className="ai-chat-input-row">
          <button className="ai-chat-btn-image" onClick={() => fileInputRef.current?.click()} title={t('aiCoach.chat.uploadImage')}>
            &#128247;
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('aiCoach.chat.placeholder')}
            disabled={isStreaming}
            rows={1}
          />
          <button
            className="ai-chat-btn-send"
            onClick={() => sendMessage()}
            disabled={isStreaming || (!input.trim() && images.length === 0)}
            title={t('aiCoach.chat.send')}
          >
            &#10148;
          </button>
        </div>
      </div>

      <WeeklyReview
        isOpen={showWeeklyReview}
        onClose={() => setShowWeeklyReview(false)}
        onDiscuss={handleWeeklyReviewDiscuss}
        onGenerateWeek={handleWeeklyReviewGenerate}
      />
    </div>
  );
};

export default AICoachChat;
